/**
 * Colour and motion helpers for the sign-in field panel.
 *
 * The panel carries white text on the tenant's `secondary` colour. A tenant
 * can set any secondary, including a pale one, so the colour is darkened
 * until white text on it reaches the WCAG 4.5:1 contrast for body text.
 */
const HEX_COLOUR = /^#[0-9a-fA-F]{6}$/;
const MIN_CONTRAST_WITH_WHITE = 4.5;
const DARKEN_STEP = 0.9;
const MAX_STEPS = 30;

function channel(value: number): number {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function parse(hex: string): [number, number, number] {
  return [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number];
}

function toHex(rgb: [number, number, number]): string {
  return `#${rgb.map((c) => Math.round(c).toString(16).padStart(2, "0")).join("")}`;
}

/** WCAG relative luminance of an `#rrggbb` colour. */
export function luminance(hex: string): number {
  const [r, g, b] = parse(hex).map(channel) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** The contrast ratio of white text on an `#rrggbb` colour. */
export function contrastWithWhite(hex: string): number {
  return 1.05 / (luminance(hex) + 0.05);
}

/**
 * The panel background for a tenant secondary colour. A valid colour that is
 * already dark enough is used as it is (Boer `#0A3F3A`). A pale one is
 * darkened. No valid colour → the `--tenant-secondary` token that
 * `applyBranding` always sets.
 */
export function panelBackground(secondary: string | null | undefined): string {
  if (!secondary || !HEX_COLOUR.test(secondary)) return "var(--tenant-secondary)";
  let rgb = parse(secondary);
  for (let step = 0; step < MAX_STEPS && contrastWithWhite(toHex(rgb)) < MIN_CONTRAST_WITH_WHITE; step += 1) {
    rgb = rgb.map((c) => c * DARKEN_STEP) as [number, number, number];
  }
  return toHex(rgb).toUpperCase();
}

/** True when the visitor asked the system for reduced motion. */
export function prefersReducedMotion(): boolean {
  try {
    return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}
